import z from "zod";
import { API } from "./api";
import { JSONBody } from "./general";
import { convertScanner, Scanner } from "./scanner";

export enum TimelapseStatus {
  STARTED = "started",
  PAUSED = "paused",
  FINISHED = "finished",
}

export const getTimelapseStatusText = (status: TimelapseStatus): string => {
  switch (status) {
    case TimelapseStatus.STARTED:
      return "Running"
    case TimelapseStatus.PAUSED:
      return "Paused"
    case TimelapseStatus.FINISHED:
      return "Finished"
  }
}

export const getTimelapseStatusColor = (status: TimelapseStatus): string => {
  switch (status) {
    case TimelapseStatus.STARTED:
      return "green"
    case TimelapseStatus.PAUSED:
      return "orange"
    case TimelapseStatus.FINISHED:
      return "red"
  }
}

export const getTimelapseBackgroundColor = (status: TimelapseStatus): string => {
  switch (status) {
    case TimelapseStatus.STARTED:
      return "bg-green-500"
    case TimelapseStatus.PAUSED:
      return "bg-orange-500"
    case TimelapseStatus.FINISHED:
      return "bg-red-500"
  }
}

export interface Timelapse {
  id: number;
  coverId?: number;
  name: string;
  description?: string;
  status: TimelapseStatus
  scanner: Scanner;
  scans: number;
  lastScan?: Date;
  createdAt: Date;
  deletedAt?: Date;
}

export interface TimelapseFilter {
  status?: TimelapseStatus;
  scanner?: number;
  deleted?: boolean;
}

export const convertTimelapse = (t: API.Timelapse): Timelapse => {
  return {
    id: t.id,
    coverId: t.cover_id,
    name: t.name,
    description: t.description,
    status: t.status as TimelapseStatus,
    scanner: convertScanner(t.scanner),
    scans: t.scans,
    lastScan: t.last_scan ? new Date(t.last_scan) : undefined,
    createdAt: new Date(t.created_at),
    deletedAt: t.deleted_at ? new Date(t.deleted_at) : undefined,
  }
}

export const convertTimelapses = (t: API.Timelapse[]): Timelapse[] => {
  return t.map(convertTimelapse).sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime())
}

// Schema

export const timelapseCreateSchema = z.object({
  areaId: z.number().positive(),
  name: z.string().nonempty(),
  description: z.string().optional(),
})
export type TimelapseCreateSchema = z.infer<typeof timelapseCreateSchema> & JSONBody

export const convertTimelapseModel = (t: Timelapse): TimelapseUpdateSchema => {
  return {
    id: t.id,
    coverId: t.coverId,
    name: t.name,
    description: t.description,
    status: t.status,
  }
}

export const timelapseUpdateSchema = z.object({
  id: z.number().positive(),
  coverId: z.number().positive().optional(),
  name: z.string().nonempty(),
  description: z.string().optional(),
  status: z.enum(TimelapseStatus),
})
export type TimelapseUpdateSchema = z.infer<typeof timelapseUpdateSchema> & JSONBody
